/**
 * PostHog analytics for the web app (Phase 2.3 event taxonomy).
 *
 * Mirrors the lazy-init pattern in `sentry.ts`: the SDK is dynamically imported
 * only when `VITE_POSTHOG_KEY` is set at build time, so the launcher chunk is
 * unchanged in builds without telemetry. All calls are fire-and-forget — no
 * await ever blocks the calling code path.
 *
 * Event taxonomy: see Phase 2.3 spec. All match-lifecycle events are prefixed
 * `match_*`; Phase 1 events (daily / freeplay / share / lobby) are also wired.
 *
 * Privacy: only the Telegram user_id (a numeric string) is sent as distinctId.
 * Never include first_name, username, or any other PII in event properties.
 */

type PostHogLike = {
  init: (key: string, opts: Record<string, unknown>) => void
  capture: (event: string, props?: Record<string, unknown>) => void
  identify: (id: string, props?: Record<string, unknown>) => void
}

let initialized = false
let posthogPromise: Promise<PostHogLike | null> | null = null

function loadPosthog(): Promise<PostHogLike | null> {
  if (posthogPromise) return posthogPromise
  posthogPromise = import('posthog-js')
    .then((mod) => (mod.default ?? mod) as unknown as PostHogLike)
    .catch(() => null)
  return posthogPromise
}

export async function initAnalytics(): Promise<void> {
  if (initialized) return
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) return

  const host: string =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://app.posthog.com'

  try {
    const posthog = await loadPosthog()
    if (!posthog) return
    posthog.init(key, {
      api_host: host,
      // Don't auto-capture pageviews / clicks — we send explicit events only.
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      // Telegram WebApp WebViews can be flaky on persistence; sessionStorage
      // is the safest default.
      persistence: 'localStorage+cookie',
      disable_session_recording: true,
    })
    initialized = true

    // Tag distinctId with Telegram user_id if available (sanitized — no first_name).
    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
    const uid = tg?.initDataUnsafe?.user?.id
    if (uid) {
      posthog.identify(String(uid))
    }
  } catch (err) {
    // Never let analytics init failures block app boot.
    // eslint-disable-next-line no-console
    console.warn('[analytics] init failed:', err)
  }
}

/**
 * Set / update PostHog identity. Call after a successful Telegram initData
 * verification when the user_id is known. No-op if analytics isn't initialized.
 */
export function setUser(id: number | string, props?: Record<string, unknown>): void {
  if (!initialized) return
  void loadPosthog().then((posthog) => {
    if (!posthog) return
    try {
      posthog.identify(String(id), props)
    } catch {
      /* swallow */
    }
  })
}

/**
 * Fire-and-forget event capture. No-op if analytics isn't initialized.
 * Never throws, never awaits — safe to call from any render or handler.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialized) return
  void loadPosthog().then((posthog) => {
    if (!posthog) return
    try {
      posthog.capture(event, props)
    } catch {
      /* swallow */
    }
  })
}
